import { sendIsTyping, sendWidgetReply } from 'flow/actions/channels-actions';
import EmojiSearch from 'flow/components/messages/EmojiSearch';
import useAppContext from 'flow/hooks/useAppContext';
import useAutosizeTextArea from 'flow/hooks/useAutosizeTextArea';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import EmojiPicker from 'flow/pages/messages/EmojiPicker';
import SendButton from 'flow/pages/messages/SendButton';
import { selectChatClientKey } from 'flow/selectors/chat-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { ChannelKey, ChatSessionKey } from 'flow/types/EntityKeys';
import React, {
    KeyboardEvent,
    Suspense,
    useEffect,
    useRef,
    useState,
} from 'react';

interface Props {
    chatSessionKey: ChatSessionKey;
    channelKey: ChannelKey;
}

const MessageInput = ({ chatSessionKey, channelKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const clientKey = useAppSelector(selectChatClientKey);

    const [messageContent, setMessageContent] = useState('');
    const [isTypingToggled, setIsTypingToggled] = useState(false);

    const [cursorPosition, setCursorPosition] = useState<number | null>(null);

    useAutosizeTextArea(textAreaRef, messageContent);

    const updateMessageAndPosition = (text: string, index: number) => {
        setMessageContent(text);
        setCursorPosition(index);
    };

    useEffect(() => {
        if (cursorPosition !== null && textAreaRef.current) {
            textAreaRef.current.focus();
            textAreaRef.current.setSelectionRange(
                cursorPosition,
                cursorPosition,
            );
            setCursorPosition(null);
        }
    }, [cursorPosition, messageContent]);

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const handleSend = () => {
        setMessageContent('');
        dispatch(
            sendWidgetReply(
                chatSessionKey,
                clientKey,
                messageContent,
                channelKey,
            ),
        );
        dispatch(sendIsTyping(chatSessionKey, ownExtensionKey, false));
        setIsTypingToggled(false);
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const message = e.target.value;
        const isTyping = !!message.length;
        setMessageContent(message);

        if ((isTyping && !isTypingToggled) || (!isTyping && isTypingToggled)) {
            dispatch(sendIsTyping(chatSessionKey, ownExtensionKey, isTyping));
            setIsTypingToggled(isTyping);
        }
    };

    return (
        <div className="relative w-full max-w-2xl">
            <form onSubmit={handleSend}>
                <div className="relative flex w-full items-start gap-x-1 bg-gray-200 p-2 first:rounded-t-sm last:rounded-b-sm sm:gap-x-3 sm:p-3">
                    <div className="relative flex h-full min-w-0 flex-1">
                        <textarea
                            id="chat-message"
                            aria-label={i18next.t('message.input.send_to')}
                            className="h-fit max-h-24 w-full min-w-0 flex-1 resize-none overflow-y-scroll bg-transparent pt-1 text-base text-wrap text-gray-800 placeholder:truncate placeholder:text-gray-500 focus:border-transparent focus:ring-0 focus:outline-hidden"
                            onChange={handleTextChange}
                            onKeyDown={handleKeyDown}
                            ref={textAreaRef}
                            rows={1}
                            value={messageContent}
                            placeholder="Aa"
                        />
                    </div>

                    <Suspense fallback={<></>}>
                        <EmojiSearch
                            textAreaRef={textAreaRef}
                            messageContent={{ text: messageContent }}
                            updateMessage={updateMessageAndPosition}
                        />
                    </Suspense>

                    <div className="flex items-center gap-x-1 sm:gap-x-2">
                        <Suspense fallback={<></>}>
                            <EmojiPicker
                                textAreaRef={textAreaRef}
                                messageContent={{ text: messageContent }}
                                updateMessage={updateMessageAndPosition}
                            />
                        </Suspense>

                        <SendButton
                            message={{ text: messageContent }}
                            handleSend={handleSend}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default MessageInput;
